<template>
  <HLModal
    :show="isOpen"
    :show-header="true"
    :show-footer="true"
    :show-close="true"
    :mask-closable="true"
    :close-on-esc="true"
    :showHeaderIcon="false"
    width="600"
    class="project-selector-modal"
    @update:show="(value) => !value && $emit('cancel')"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="project" class="w-4 h-4 text-white" />
        </div>
        <div>
          <span class="text-lg font-bold text-gray-900 dark:text-white">
            {{ title }}
          </span>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {{ description }}
          </p>
        </div>
      </div>
    </template>

    <template #default>
      <div class="space-y-4 px-6">
        <!-- Helpful Note -->
        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p class="text-sm text-blue-900 dark:text-blue-200">
            <span class="font-semibold">💡 Tip:</span> Before creating a new bug, please check the existing bugs list to ensure there are no duplicates. 
            <HLButton
              variant="text"
              color="blue"
              size="sm"
              class="inline p-0 h-auto font-semibold underline"
              @click="goToBugsList"
            >
              View Bugs List
            </HLButton>
          </p>
        </div>

        <!-- Project Select Dropdown -->
        <div>
          <HLText as="label" size="lg" weight="medium" class="block mb-6">
            Select a Project <span class="text-red-500">*</span>
          </HLText>
          <HLSelect
            v-model:value="selectedProjectId"
            :options="projectOptions"
            placeholder="Search and select a project..."
            filterable
            searchable
            size="sm"
            class="w-full mt-3"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3 pt-2 pb-4 pr-4">
        <HLButton
          variant="secondary"
          size="md"
          @click="$emit('cancel')"
        >
          Cancel
        </HLButton>
        <HLButton
          variant="primary"
          color="blue"
          size="md"
          :disabled="!selectedProjectId"
          @click="confirmSelection"
        >
          Continue
        </HLButton>
      </div>
    </template>
  </HLModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { HLModal, HLButton, HLSelect, HLText } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'

const router = useRouter()

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Select a Project'
  },
  description: {
    type: String,
    default: 'Choose a project to continue'
  },
  projects: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['confirm', 'cancel'])

const selectedProjectId = ref('')

const projectOptions = computed(() => {
  return props.projects.map(project => ({
    label: project.name,
    value: project.id,
    renderLabel: () => (
      `${project.name}${project.description ? ' — ' + project.description : ''}`
    )
  }))
})

const selectedProject = computed(() => {
  return props.projects.find(p => p.id === selectedProjectId.value)
})

const confirmSelection = () => {
  if (selectedProject.value) {
    emit('confirm', selectedProject.value)
    selectedProjectId.value = ''
  }
}

const goToBugsList = () => {
  router.push('/bugs')
}

// Reset on close
const resetModal = () => {
  selectedProjectId.value = ''
}

// Watch for modal close
watch(() => props.isOpen, (newVal) => {
  if (!newVal) {
    resetModal()
  }
})
</script>

<style scoped>
/* Hide the default info icon from HLModal header */
:deep(.hl-modal__header .hl-icon) {
  display: none !important;
}

/* Increase HLSelect font size */
:deep(.hr-select) {
  font-size: 1rem !important;
}

:deep(.hr-select .hr-base-selection-input__content) {
  font-size: 1rem !important;
}

:deep(.hr-select .hr-base-selection-label) {
  font-size: 1rem !important;
}

:deep(.hr-select-option) {
  font-size: 1rem !important;
  padding: 0.75rem 1rem !important;
}

/* Increase gap between dropdown options */
:deep(.hr-select-options) {
  gap: 0.5rem !important;
}

:deep(.hr-base-select__menu) {
  gap: 0.5rem !important;
}
</style>

